import { z } from 'zod';
import {
  ApplicationEnvironmentSchema,
  HexColorSchema,
  IsoTimestampSchema,
  UuidSchema,
} from './common';

export const AI_TOKEN_STATUSES = ['active', 'paused', 'revoked'] as const;
export const AiTokenStatusSchema = z.enum(AI_TOKEN_STATUSES);
export type AiTokenStatus = z.infer<typeof AiTokenStatusSchema>;

export const AI_COST_SOURCES = ['client', 'computed', 'estimated'] as const;
export const AiCostSourceSchema = z.enum(AI_COST_SOURCES);
export type AiCostSource = z.infer<typeof AiCostSourceSchema>;

export const AI_PRICE_SOURCES = ['official-api', 'litellm', 'manual', 'estimated'] as const;
export const AiPriceSourceSchema = z.enum(AI_PRICE_SOURCES);
export type AiPriceSource = z.infer<typeof AiPriceSourceSchema>;

export const AI_PRICING_UNITS = ['tokens', 'audio_seconds', 'characters'] as const;
export const AiPricingUnitSchema = z.enum(AI_PRICING_UNITS);
export type AiPricingUnit = z.infer<typeof AiPricingUnitSchema>;

export const AI_USAGE_RANGES = ['1h', '6h', '24h', '7d', '30d', 'mtd'] as const;
export const AiUsageRangeSchema = z.enum(AI_USAGE_RANGES);
export type AiUsageRange = z.infer<typeof AiUsageRangeSchema>;

const ProviderModelString = z
  .string()
  .min(1)
  .max(128)
  .regex(/^[a-z0-9][a-z0-9._-]*$/i, 'letters, digits, dot, dash or underscore');

// --- Tokens ---

export const AiUsageTokenSchema = z.object({
  id: UuidSchema,
  applicationId: UuidSchema,
  label: z.string(),
  tokenPrefix: z.string(),
  status: AiTokenStatusSchema,
  createdAt: IsoTimestampSchema,
  lastUsedAt: IsoTimestampSchema.nullable(),
});

export const AiUsageTokenWithStatsSchema = AiUsageTokenSchema.extend({
  applicationName: z.string(),
  applicationProduct: z.string(),
  applicationCompany: z.string(),
  applicationInstance: z.string(),
  applicationEnvironment: ApplicationEnvironmentSchema,
  events24h: z.number().int().nonnegative(),
  cost24hUsd: z.number().nonnegative(),
});

export const CreateAiTokenSchema = z.object({
  applicationId: UuidSchema,
  label: z.string().min(1).max(64),
});

export const UpdateAiTokenSchema = z
  .object({
    status: AiTokenStatusSchema.optional(),
    label: z.string().min(1).max(64).optional(),
  })
  .refine((v) => v.status !== undefined || v.label !== undefined, {
    message: 'At least one of status or label must be provided',
  });

export const AiTokenCreatedResponseSchema = z.object({
  token: AiUsageTokenSchema,
  rawToken: z.string(),
});

export type AiUsageToken = z.infer<typeof AiUsageTokenSchema>;
export type AiUsageTokenWithStats = z.infer<typeof AiUsageTokenWithStatsSchema>;
export type CreateAiToken = z.infer<typeof CreateAiTokenSchema>;
export type UpdateAiToken = z.infer<typeof UpdateAiTokenSchema>;
export type AiTokenCreatedResponse = z.infer<typeof AiTokenCreatedResponseSchema>;

// --- Prices ---

export const AiModelPriceSchema = z.object({
  id: UuidSchema,
  provider: z.string(),
  model: z.string(),
  inputPricePer1m: z.number().nonnegative(),
  outputPricePer1m: z.number().nonnegative(),
  cachedInputPricePer1m: z.number().nonnegative().nullable(),
  pricingUnit: AiPricingUnitSchema,
  currency: z.string(),
  source: AiPriceSourceSchema,
  lastSyncedAt: IsoTimestampSchema.nullable(),
  updatedAt: IsoTimestampSchema,
});

export const PriceSyncRunSchema = z.object({
  id: UuidSchema,
  startedAt: IsoTimestampSchema,
  finishedAt: IsoTimestampSchema.nullable(),
  sourceName: z.string(),
  modelsAdded: z.number().int().nonnegative(),
  modelsChanged: z.number().int().nonnegative(),
  modelsUnchanged: z.number().int().nonnegative(),
  modelsSkippedLowerRank: z.number().int().nonnegative(),
  errors: z.array(
    z.object({
      provider: z.string().optional(),
      model: z.string().optional(),
      message: z.string(),
    }),
  ),
  ok: z.boolean(),
});

export const UnpricedModelSchema = z.object({
  provider: z.string(),
  model: z.string(),
  displayLabel: z.string(),
  events7d: z.number().int().nonnegative(),
  suggestedFrom: z
    .object({
      provider: z.string(),
      model: z.string(),
      inputPricePer1m: z.number().nonnegative(),
      outputPricePer1m: z.number().nonnegative(),
      cachedInputPricePer1m: z.number().nonnegative().nullable(),
      pricingUnit: AiPricingUnitSchema,
    })
    .nullable(),
});

export type PriceSyncRun = z.infer<typeof PriceSyncRunSchema>;
export type UnpricedModel = z.infer<typeof UnpricedModelSchema>;

export const UpsertAiPriceSchema = z.object({
  provider: ProviderModelString,
  model: ProviderModelString,
  inputPricePer1m: z.number().nonnegative(),
  outputPricePer1m: z.number().nonnegative(),
  cachedInputPricePer1m: z.number().nonnegative().nullable().optional(),
  pricingUnit: AiPricingUnitSchema.optional(),
  source: AiPriceSourceSchema.optional(),
});

export type AiModelPrice = z.infer<typeof AiModelPriceSchema>;
export type UpsertAiPrice = z.infer<typeof UpsertAiPriceSchema>;

// --- Models catalog (auto-populated from ingest) ---

export const AiModelSchema = z.object({
  id: UuidSchema,
  provider: z.string(),
  model: z.string(),
  displayLabel: z.string(),
  color: z.string(),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema,
});

export const AiModelWithStatsSchema = AiModelSchema.extend({
  events24h: z.number().int().nonnegative(),
  cost24hUsd: z.number().nonnegative(),
});

export const UpdateAiModelSchema = z
  .object({
    displayLabel: z.string().min(1).max(128).optional(),
    color: HexColorSchema.optional(),
  })
  .refine((v) => v.displayLabel !== undefined || v.color !== undefined, {
    message: 'At least one of displayLabel or color must be provided',
  });

export type AiModel = z.infer<typeof AiModelSchema>;
export type AiModelWithStats = z.infer<typeof AiModelWithStatsSchema>;
export type UpdateAiModel = z.infer<typeof UpdateAiModelSchema>;

// --- Ingest ---

export const AiUsageIngestSchema = z
  .object({
    provider: ProviderModelString,
    model: ProviderModelString,
    inputTokens: z.number().int().nonnegative().optional(),
    outputTokens: z.number().int().nonnegative().optional(),
    cachedInputTokens: z.number().int().nonnegative().optional(),
    audioSeconds: z.number().nonnegative().optional(),
    characters: z.number().int().nonnegative().optional(),
    costUsd: z.number().nonnegative().optional(),
    timestamp: IsoTimestampSchema.optional(),
    metadata: z.record(z.unknown()).optional(),
  })
  .refine(
    (v) =>
      (v.inputTokens ?? 0) > 0 ||
      (v.outputTokens ?? 0) > 0 ||
      (v.audioSeconds ?? 0) > 0 ||
      (v.characters ?? 0) > 0 ||
      v.costUsd !== undefined,
    {
      message:
        'At least one of inputTokens, outputTokens, audioSeconds, characters or costUsd must be provided',
    },
  );

export const AiUsageIngestBatchSchema = z.object({
  events: z.array(AiUsageIngestSchema).min(1).max(500),
});

export const AiUsageIngestResponseSchema = z.object({
  id: UuidSchema,
  costUsd: z.number().nullable(),
  costSource: AiCostSourceSchema,
});

export const AiUsageIngestBatchResponseSchema = z.object({
  ids: z.array(UuidSchema),
  rejected: z.array(
    z.object({
      index: z.number().int().nonnegative(),
      error: z.string(),
    }),
  ),
});

export type AiUsageIngest = z.infer<typeof AiUsageIngestSchema>;
export type AiUsageIngestBatch = z.infer<typeof AiUsageIngestBatchSchema>;
export type AiUsageIngestResponse = z.infer<typeof AiUsageIngestResponseSchema>;
export type AiUsageIngestBatchResponse = z.infer<typeof AiUsageIngestBatchResponseSchema>;

// --- Events (admin listing) ---

export const AiUsageEventSchema = z.object({
  id: UuidSchema,
  applicationId: UuidSchema,
  applicationName: z.string(),
  aiTokenId: UuidSchema.nullable(),
  provider: z.string(),
  model: z.string(),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  cachedInputTokens: z.number().int().nonnegative(),
  audioSeconds: z.number().nonnegative(),
  characters: z.number().int().nonnegative(),
  costUsd: z.number().nullable(),
  costSource: AiCostSourceSchema,
  timestamp: IsoTimestampSchema,
  metadata: z.record(z.unknown()),
});

export type AiUsageEvent = z.infer<typeof AiUsageEventSchema>;

// --- Summary ---

export const AiUsageSummaryQuerySchema = z.object({
  range: AiUsageRangeSchema.default('24h'),
  applicationId: UuidSchema.optional(),
  provider: z.string().optional(),
  model: z.string().optional(),
  environment: ApplicationEnvironmentSchema.optional(),
});

const AiUsageTotalsSchema = z.object({
  events: z.number().int().nonnegative(),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  cachedInputTokens: z.number().int().nonnegative(),
  audioSeconds: z.number().nonnegative(),
  characters: z.number().int().nonnegative(),
  costUsd: z.number().nonnegative(),
});

const AiUsageBucketSchema = z.object({
  bucket: IsoTimestampSchema,
  events: z.number().int().nonnegative(),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  audioSeconds: z.number().nonnegative(),
  characters: z.number().int().nonnegative(),
  costUsd: z.number().nonnegative(),
});

const AiUsageByApplicationSchema = z.object({
  applicationId: UuidSchema,
  applicationName: z.string(),
  events: z.number().int().nonnegative(),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  audioSeconds: z.number().nonnegative(),
  characters: z.number().int().nonnegative(),
  costUsd: z.number().nonnegative(),
});

const AiUsageByModelSchema = z.object({
  provider: z.string(),
  model: z.string(),
  displayLabel: z.string(),
  color: z.string(),
  pricingUnit: AiPricingUnitSchema,
  events: z.number().int().nonnegative(),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  audioSeconds: z.number().nonnegative(),
  characters: z.number().int().nonnegative(),
  costUsd: z.number().nonnegative(),
});

const AiUsageAllTimeSchema = z.object({
  events: z.number().int().nonnegative(),
  inputTokens: z.number().int().nonnegative(),
  audioSeconds: z.number().nonnegative(),
  characters: z.number().int().nonnegative(),
  costUsd: z.number().nonnegative(),
});

export const AiUsageSummarySchema = z.object({
  totals: AiUsageTotalsSchema,
  timeline: z.array(AiUsageBucketSchema),
  byApplication: z.array(AiUsageByApplicationSchema),
  byModel: z.array(AiUsageByModelSchema),
  allTime: AiUsageAllTimeSchema.optional(),
});

export type AiUsageSummaryQuery = z.infer<typeof AiUsageSummaryQuerySchema>;
export type AiUsageSummary = z.infer<typeof AiUsageSummarySchema>;
